import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import ItineraryMap from 'components/Cruises/CruiseOfferPage/content/CruiseItinerary/ItineraryMap'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { getCruiseUniqueItineraryList } from 'lib/cruises/cruiseUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import Image from 'components/Common/Image'
import { mediaQueryUp } from 'components/utils/breakpoint'
import clsx from 'clsx'
import { isCruiseV1Offer } from 'lib/offer/offerTypes'
import { EmptyArray } from 'lib/array/arrayUtils'

const Itinerary = styled.div`
  order: 4;
  padding: ${rem(24)} ${rem(24)} 0 ${rem(24)};
`

const ImageItinerary = styled.div`
  width: 100%;

  img {
    width: 100%;
    height: auto;
  }
`

const Sticky = styled.div`
  ${mediaQueryUp.desktop} {
    position: sticky;
    top: 0;
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${rem(60)} ${rem(65)} auto;
  gap: ${rem(10)};

  .colSpan {
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(56)} ${rem(60)} auto;
  }
`

interface Props {
  offerTitle: string
  itinerary?: Array<App.CruiseItineraryItem>
  itineraryFlash?: string
  offer: App.CruiseOffer | App.Offer | App.OfferSummary | App.Cruises.CruiseOfferSnapshot
}

function CruiseModalItineraryDetails({
  itinerary = EmptyArray,
  offerTitle,
  itineraryFlash,
  offer,
}: Props) {
  const intl = useIntl()
  const isOfferSnapshot = !('type' in offer)
  const isFlashOffer = !isOfferSnapshot && isCruiseV1Offer(offer)
  const uniqueItineraryList = getCruiseUniqueItineraryList(
    itinerary,
    isFlashOffer,
  )

  const shortItinerartFlashOffer = useMemo(() => {
    const pattern = /Day\s\d+(?:\s&\s\d+)?:\s([^*]+)/g
    const matches = itineraryFlash?.match(pattern)
    const mappedMatchs = matches?.map(match => match.replace(':', '&nbsp;&nbsp;&nbsp;'))!
    return mappedMatchs?.join('\n\n') ?? ''
  }, [itineraryFlash])

  const hasPort = useMemo(() => !!itinerary?.length && itinerary.some(item => !!item.port), [itinerary])

  const modalTitle = intl.formatMessage(
    {
      id: 'A1bH-XodOxYIsEqISi_O',
      defaultMessage: 'Itinerary for {offerTitle}',
      description: 'Modal title showing cruise itinerary details for a specific offer',
    },
    { offerTitle },
  )

  const atSeaLabel = intl.formatMessage({
    id: 'eglatGtb5bLey9o-rWTz',
    defaultMessage: 'At Sea',
    description: 'Itinerary label for days spent at sea during a cruise',
  })

  const viewOfferLabel = intl.formatMessage({
    id: 'oIdlXjzrywUoHocQ2ziJ',
    defaultMessage: 'View offer',
    description: 'Button text to view the full cruise offer from the itinerary modal',
  })

  return <ModalBase height="auto">
    <ModalHeader
      title={modalTitle}
    />
    <ModalBody>

      {!isFlashOffer && hasPort && <Sticky>
        <ItineraryMap
          isModal
          itinerary={itinerary}
        />
      </Sticky>}
      {isFlashOffer && !!offer.images[1] && <Sticky>
        <ImageItinerary>
          <Image
            data-testid="cruise-modal-itinerary-image"
            quality="eco"
            dpr={2}
            enhancedFormats={false}
            image={offer.images[1]}
          />
        </ImageItinerary>
      </Sticky>}

      <Itinerary>
        <Grid>
          {!isFlashOffer && uniqueItineraryList.map((itinerary) => <>
            <BodyText variant="medium">
              {intl.formatMessage(
                {
                  id: 'ZXgIvBZ2-xbRyWFDcP9t',
                  defaultMessage: 'Day {day}',
                  description: 'Itinerary day number label in cruise itinerary modal',
                },
                { day: itinerary.item.startDay },
              )}
            </BodyText>
            {(itinerary.isStart || itinerary.isEnd || itinerary.addPortCircle) && <BodyText variant="medium" weight="bold">
              {itinerary.isStart && intl.formatMessage({
                id: '1Xrss1qm4dKd84CvWkUK',
                defaultMessage: 'Start:',
                description: 'Label prefix for starting port in cruise itinerary',
              })}
              {itinerary.isEnd && intl.formatMessage({
                id: 'a6jwiioMVJjN1rL2byrQ',
                defaultMessage: 'End:',
                description: 'Label prefix for ending port in cruise itinerary in the cruises section',
              })}
              {itinerary.addPortCircle && !itinerary.isStart && !itinerary.isEnd && intl.formatMessage(
                {
                  id: 'T1PAnURmPrNAmocq7m_l',
                  defaultMessage: 'Port {portNumber}:',
                  description: 'Port number label prefix in cruise itinerary, e.g. Port 3:',
                },
                { portNumber: itinerary.portNumber },
              )}
            </BodyText>}

            <BodyText variant="medium" format="titlecase" className={clsx({ colSpan: !itinerary.isStart && !itinerary.isEnd && !itinerary.addPortCircle })}>
              {!!itinerary.item.title && itinerary.item.title.toLowerCase()}
              {!itinerary.item.title && atSeaLabel}
            </BodyText>
          </>)}
        </Grid>

        {isFlashOffer && shortItinerartFlashOffer && <MarkdownRender type="compact" fontSize="large" content={shortItinerartFlashOffer} />}
      </Itinerary>
    </ModalBody>
    <ModalFooter
      isPrimaryActionHidden={isOfferSnapshot}
      primaryActionProps={{
        children: viewOfferLabel,
        href: offerPageURL(isOfferSnapshot ? undefined : offer),
        target: '_blank',
      }}
    />
  </ModalBase>
}

export default CruiseModalItineraryDetails
